import axios from 'axios';

// Same-origin backend. In dev, CRA proxies /api -> the Express backend (see
// package.json "proxy"); in production the backend serves the SPA and /api together.
const API_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach the Clerk session token (when signed in) as a Bearer on every request,
// so gated actions (issue, manage credential types) authenticate. Clerk exposes
// the active session on `window.Clerk` once ClerkProvider has mounted. Falls back
// to whatever Authorization the caller set (legacy operator token).
api.interceptors.request.use(async (config) => {
  try {
    const session = typeof window !== 'undefined' && window.Clerk && window.Clerk.session;
    if (session) {
      const token = await session.getToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
    }
  } catch (e) { /* not signed in — leave existing auth as-is */ }
  return config;
});

// Check if the service is online
export const checkServiceStatus = async () => {
  try {
    const response = await api.get('/health');
    return response.status === 200;
  } catch (error) {
    console.error('Health check failed:', error);
    return false;
  }
};

// DID Web operations
export const resolveDid = async (did) => {
  try {
    const response = await api.get(`/did/${did}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const signMessage = async (did, message) => {
  try {
    const response = await api.post('/sign', { did, message });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Authentication operations
export const login = async (username, password) => {
  try {
    const response = await api.post('/auth/login', { username, password });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const verifyToken = async (token) => {
  try {
    const response = await api.get('/auth/verify', {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Helper function to get authentication headers
export const getAuthHeaders = () => {
  const token = localStorage.getItem('vc_admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Credential request operations
export const createCredentialRequest = async (data) => {
  try {
    const response = await api.post('/credential-requests', data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getCredentialRequest = async (id) => {
  try {
    const response = await api.get(`/credential-requests/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Console-owned request round trip (create is public; list/approve/reject need auth).
export const listCredentialRequests = async () => {
  const response = await api.get('/credential-requests', { headers: getAuthHeaders() });
  return response.data;
};

export const getPendingCredentialRequests = async () => {
  try {
    const response = await api.get('/credential-requests/pending', {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getCredentialRequestsByStatus = async (status) => {
  try {
    const response = await api.get(`/credential-requests/status/${status}`, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getCredentialRequestsForSubject = async (subject) => {
  try {
    const response = await api.get(`/credential-requests/subject/${subject}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const approveCredentialRequest = async (id, approverDid) => {
  try {
    const response = await api.post(`/credential-requests/${id}/approve`, 
      { approverDid },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const rejectCredentialRequest = async (id, rejectorDid, reason) => {
  try {
    const response = await api.post(`/credential-requests/${id}/reject`, 
      { rejectorDid, reason },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Credential operations
export const issueCredential = async (data) => {
  try {
    const response = await api.post('/credentials/issue', data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const verifyCredential = async (credential) => {
  try {
    const response = await api.post('/credentials/verify', { credential });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getCredential = async (id) => {
  try {
    const response = await api.get(`/credentials/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getCredentialStatus = async (id) => {
  try {
    const response = await api.get(`/credentials/${id}/status`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const revokeCredential = async (id, issuerDid, reason) => {
  try {
    const data = { issuerDid };
    if (reason) {
      data.reason = reason;
    }
    const response = await api.post(`/credentials/${id}/revoke`, data, { headers: getAuthHeaders() });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Signing Credential Management
export const getSigningCredentials = async () => {
  try {
    const response = await api.get('/signing-credentials', { headers: getAuthHeaders() });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getSigningCredential = async (id) => {
  try {
    const response = await api.get(`/signing-credentials/${id}`, { headers: getAuthHeaders() });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getDefaultSigningCredential = async () => {
  try {
    const response = await api.get('/signing-credentials/default', { headers: getAuthHeaders() });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createSigningCredential = async (credentialData) => {
  try {
    const response = await api.post('/signing-credentials', credentialData, { headers: getAuthHeaders() });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const uploadCertificate = async (formData) => {
  try {
    const response = await api.post('/signing-credentials/upload', formData, {
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const setDefaultSigningCredential = async (credentialId) => {
  try {
    const response = await api.post('/signing-credentials/default', { credentialId }, { headers: getAuthHeaders() });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteSigningCredential = async (id) => {
  try {
    const response = await api.delete(`/signing-credentials/${id}`, { headers: getAuthHeaders() });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Credential type templates (owned by this console's backend / Postgres)
export const getCredentialTypes = async () => {
  const response = await api.get('/credential-types');
  return response.data;
};

export const getCredentialType = async (id) => {
  const response = await api.get(`/credential-types/${id}`);
  return response.data;
};

export const createCredentialType = async (data) => {
  const response = await api.post('/credential-types', data, { headers: getAuthHeaders() });
  return response.data;
};

export const updateCredentialType = async (id, data) => {
  const response = await api.put(`/credential-types/${id}`, data, { headers: getAuthHeaders() });
  return response.data;
};

export const deleteCredentialType = async (id) => {
  const response = await api.delete(`/credential-types/${id}`, { headers: getAuthHeaders() });
  return response.data;
};

// Non-secret per-customer branding/config surfaced by the backend
export const getAppConfig = async () => {
  const response = await api.get('/config');
  return response.data;
};

// Setup flow: discover the org's trust.txt, load members, report status.
export const discoverNetwork = async () => {
  const response = await api.get('/network/discover');
  return response.data;
};

export const loadNetwork = async () => {
  const response = await api.post('/network/load', {}, { headers: getAuthHeaders() });
  return response.data;
};

// Reset the demo: clear the loaded roster + issued credentials (back to blank).
export const resetNetwork = async () => {
  const response = await api.post('/network/reset', {}, { headers: getAuthHeaders() });
  return response.data;
};

export const getNetworkStatus = async () => {
  const response = await api.get('/network/status');
  return response.data;
};

// The loaded member roster (empty until the load step runs).
export const getMembers = async () => {
  const response = await api.get('/network/members');
  return response.data;
};

// Claim template derived from the trust graph (JTI transparency indicators).
export const getClaimTemplate = async () => {
  const response = await api.get('/network/claim-template');
  return response.data;
};

// Trust-graph member roster (the certifier's member constellation)
export const getTrustGraphMembers = async () => {
  const response = await api.get('/trust-graph/members');
  return response.data;
};

export const checkDomainInTrustGraph = async (domain) => {
  const response = await api.post('/trust-graph/check-domain', { domain });
  return response.data;
};

// Member certifications (roster-level status)
export const getCertifications = async () => {
  const response = await api.get('/certifications');
  return response.data;
};

// Issue a certification VC. opts: { credentialTypeId, signingCredentialId, validityDays, expirationDate, claims }
export const issueCertification = async (domain, opts = {}) => {
  const response = await api.post(
    '/certifications/issue',
    { domain, ...opts },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// Revoke/remove an issued certification (optionally scoped to one credential type).
export const revokeCertification = async (domain, credentialTypeId) => {
  const response = await api.delete(
    `/certifications/${encodeURIComponent(domain)}${credentialTypeId ? `?credentialTypeId=${encodeURIComponent(credentialTypeId)}` : ''}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// Look up any domain's cross-registry associations via the Trust Graph.
export const lookupDomain = async (domain) => {
  const response = await api.get(`/network/lookup?domain=${encodeURIComponent(domain)}`);
  return response.data;
};

// Live-check which member domains publish their own trust.txt. Returns { domain: bool }.
export const checkTrustTxt = async (domains) => {
  const response = await api.post('/network/trusttxt-check', { domains });
  return response.data;
};

export default api;